/**
 * season.ts (MEMBERSHIPS-02 P3) — the Season board's data layer.
 *
 * A season is one half of the membership year: FALL = the furnace checks
 * (anchor Sep 1; the tail runs into January), SPRING = the A/C checks
 * (anchor May 1; the tail runs into August). Every active plan's checks were
 * generated for its whole term (P2) — `v_membership_visit_queue` is that list
 * with the plan, the property and the status Pegasus keeps: owed → invited →
 * picked → booked → done, or snoozed / declined / forfeited.
 *
 * Season keys match the SQL (`pegasus_season_key`): year × 2, +1 for fall.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface SeasonRef {
  key: number;
  year: number;
  kind: "fall" | "spring";
  label: string;        // "Fall 2026"
  checkLabel: string;   // "Furnace Checks" (a title — Title Case; lower-case it in prose)
  category: "heating" | "cooling";
}

export function seasonFromKey(key: number): SeasonRef {
  const fall = key % 2 === 1;
  const year = Math.floor(key / 2);
  return {
    key, year, kind: fall ? "fall" : "spring",
    label: `${fall ? "Fall" : "Spring"} ${year}`,
    checkLabel: fall ? "Furnace Checks" : "A/C Checks",
    category: fall ? "heating" : "cooling",
  };
}

/** The season the office is working today: Aug–Jan = fall, Feb–Jul = spring. */
export function currentSeasonKey(today = new Date()): number {
  const m = today.getMonth() + 1;
  const y = today.getFullYear();
  if (m >= 8) return y * 2 + 1;
  if (m === 1) return (y - 1) * 2 + 1;
  return y * 2;
}

/** Default finish line: Dec 15 for a fall, Jul 15 for a spring. */
export function defaultFinish(s: SeasonRef): string {
  return s.kind === "fall" ? `${s.year}-12-15` : `${s.year}-07-15`;
}

/** MEMBERSHIPS-03 added 'deferred' (couldn't be done — a date and a reason)
 *  and 'waiting' (the office tried, the customer hasn't answered). */
export type VisitStatus = "open" | "invited" | "picked" | "booked" | "snoozed" | "deferred" | "waiting" | "declined" | "completed" | "skipped" | "forfeited" | "cancelled";

export interface SeasonVisit {
  id: string;
  custno: string;
  locno: string | null;
  agrmt_no: string;
  plan_sale_id: string | null;
  source: "esc" | "pegasus";
  task_label: string;
  category: "heating" | "cooling";
  sequence_label: string | null;
  target_date: string;
  season: string;
  season_key: number;
  target_month: number;
  status: VisitStatus;
  booked_date: string | null;
  completed_date: string | null;
  invited_at: string | null;
  nudged_at: string | null;
  picked_at: string | null;
  snoozed_until: string | null;
  deferred_to: string | null;
  esc_dispatch: string | null;
  fulfilled_dispatch: string | null;
  reason: string | null;
  term_start: string | null;
  term_end: string | null;
  days_to_term_end: number | null;
  at_risk: boolean;
  /** MEMBERSHIPS-03: the season the check was granted for, when it now rides on the current one. */
  original_season_key: number;
  carried_from: string | null;
  /** The term has ended; the check stays deliverable until owed_until. */
  term_ended: boolean;
  owed_until: string | null;
  renewal_at_this_visit: boolean;
  plan_name: string | null;
  term_years: number | null;
  systems: number | null;
  plan_status: string | null;
  plan_expire: string | null;
  customer_fullname: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  email: string | null;
  /** PLANNER-02: the tech-day this visit sits on (a saved day plan), if any. */
  placed_day_id: string | null;
  placed_tech: string | null;
  placed_date: string | null;
  placed_status: string | null;
  placed_stop_status: string | null;
}

const PAGE = 1000;

/** Every visit in a season (paged past PostgREST's 1,000-row cap). */
export function useSeasonVisits(seasonKey: number) {
  return useQuery({
    queryKey: ["season_visits", seasonKey],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<SeasonVisit[]> => {
      const out: SeasonVisit[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("v_membership_visit_queue")
          .select("*")
          .eq("season_key", seasonKey)
          .order("target_date", { ascending: true })
          .order("custno", { ascending: true })
          .order("id", { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) throw error;
        const rows = (data ?? []) as SeasonVisit[];
        out.push(...rows);
        if (rows.length < PAGE) break;
      }
      return out;
    },
  });
}

export interface SeasonPace {
  last_season: string;
  last_total: number;
  last_done_by_point: number;
  last_done_total: number;
  point_date: string;
}

/** Last year's same season at this point — from ESC's own tickets. */
export function useSeasonPace(seasonKey: number) {
  return useQuery({
    queryKey: ["season_pace", seasonKey],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<SeasonPace | null> => {
      const { data, error } = await supabase.rpc("pegasus_season_pace", { p_season_key: seasonKey });
      if (error) throw error;
      return ((data ?? []) as SeasonPace[])[0] ?? null;
    },
  });
}

/** The finish line per season, kept with the other plan settings. */
export function useSeasonFinish(seasonKey: number) {
  return useQuery({
    queryKey: ["season_finish", seasonKey],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.from("pegasus_plan_config").select("value").eq("key", "season_finish").maybeSingle();
      if (error) throw error;
      const map = ((data as { value: Record<string, string> } | null)?.value ?? {}) as Record<string, string>;
      return map[String(seasonKey)] ?? null;
    },
  });
}
export function useSetSeasonFinish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { seasonKey: number; date: string }) => {
      const { data } = await supabase.from("pegasus_plan_config").select("value").eq("key", "season_finish").maybeSingle();
      const map = { ...(((data as { value: Record<string, string> } | null)?.value ?? {}) as Record<string, string>), [String(v.seasonKey)]: v.date };
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_plan_config").upsert({ key: "season_finish", value: map, updated_by: u?.user?.email ?? null, updated_at: new Date().toISOString() }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["season_finish", v.seasonKey] }),
  });
}

export type VisitAction = "snooze" | "decline" | "defer" | "waiting" | "reopen" | "note" | "booked" | "done";

/** One write path for the board's row actions (SQL checks agreements.edit; the row is audited). */
export function useVisitAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; action: VisitAction; when?: string | null; reason?: string | null }) => {
      const { error } = await supabase.rpc("pegasus_visit_action", { p_id: v.id, p_action: v.action, p_when: v.when ?? null, p_reason: v.reason ?? null });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["season_visits"] });
      qc.invalidateQueries({ queryKey: ["plan_visits"] });
      qc.invalidateQueries({ queryKey: ["membership_card"] });
      qc.invalidateQueries({ queryKey: ["tuneups_due"] });
    },
  });
}

/** P4 — a wave of invites from the board: the next N owed visits, at-risk
 *  first, by usual month and zone. Texts first, email when there's no cell;
 *  members with neither go on the call list. HELD while comms are off. */
export interface WaveResult { invited_sms: number; invited_email: number; no_line: number; remaining: number }
export function useInviteWave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { seasonKey: number; limit: number }): Promise<WaveResult> => {
      const { data, error } = await supabase.rpc("pegasus_membership_invite_wave", { p_season_key: v.seasonKey, p_limit: v.limit });
      if (error) throw error;
      return ((data ?? []) as WaveResult[])[0] ?? { invited_sms: 0, invited_email: 0, no_line: 0, remaining: 0 };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["season_visits"] });
      qc.invalidateQueries({ queryKey: ["outbox"] });
    },
  });
}

/** The office's call list: invited and nudged a week ago with no pick, or nobody to text / email. */
export function needsCall(r: SeasonVisit, today = new Date()): boolean {
  if (r.status === "open" && (r.reason ?? "").startsWith("no text or email")) return true;
  if (r.status === "invited" && r.nudged_at) {
    const d = (today.getTime() - new Date(r.nudged_at).getTime()) / 86400000;
    return d >= 7;
  }
  return false;
}

/** Pull the mirror now (the 15-minute job does it on its own). */
export function useReconcileNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("pegasus_membership_reconcile");
      if (error) throw error;
      return ((data ?? []) as { completed: number; booked: number; linked: number; reopened: number }[])[0] ?? null;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["season_visits"] }),
  });
}

// ----------------------------------------------------------------------------
// Pure helpers the board renders with.
// ----------------------------------------------------------------------------

export const STATUS_LABEL: Record<VisitStatus, string> = {
  open: "Owed", invited: "Invited", picked: "Picked a Day", booked: "Booked", snoozed: "Snoozed",
  deferred: "Deferred", waiting: "Waiting on the Customer",
  declined: "Declined", completed: "Done", skipped: "Skipped", forfeited: "Forfeited", cancelled: "Cancelled",
};

/** Still to be done by a tech (owed or on the way to booked). */
export const isOutstanding = (s: VisitStatus) => s === "open" || s === "invited" || s === "picked" || s === "snoozed" || s === "deferred" || s === "waiting";
/** Counts toward "we still have to go out": outstanding + booked. */
export const stillToDo = (s: VisitStatus) => isOutstanding(s) || s === "booked";

export interface SeasonTotals {
  owed: number;      // every visit the season owes (all statuses except declined / cancelled / skipped)
  open: number;
  invited: number;
  picked: number;
  booked: number;
  done: number;
  snoozed: number;
  deferred: number;
  waiting: number;
  declined: number;
  forfeited: number;
  /** MEMBERSHIPS-03: owed checks carried in from an earlier season / from ended terms. */
  carried: number;
  ended: number;
  atRisk: number;
  renewals: number;
}

export function summarize(rows: SeasonVisit[]): SeasonTotals {
  const t: SeasonTotals = { owed: 0, open: 0, invited: 0, picked: 0, booked: 0, done: 0, snoozed: 0, deferred: 0, waiting: 0, declined: 0, forfeited: 0, carried: 0, ended: 0, atRisk: 0, renewals: 0 };
  for (const r of rows) {
    if (r.status !== "declined" && r.status !== "cancelled" && r.status !== "skipped") t.owed += 1;
    if (r.status === "open") t.open += 1;
    else if (r.status === "invited") t.invited += 1;
    else if (r.status === "picked") t.picked += 1;
    else if (r.status === "booked") t.booked += 1;
    else if (r.status === "completed") t.done += 1;
    else if (r.status === "snoozed") t.snoozed += 1;
    else if (r.status === "deferred") t.deferred += 1;
    else if (r.status === "waiting") t.waiting += 1;
    else if (r.status === "declined") t.declined += 1;
    else if (r.status === "forfeited") t.forfeited += 1;
    if (r.carried_from && isOutstanding(r.status)) t.carried += 1;
    if (r.term_ended && isOutstanding(r.status)) t.ended += 1;
    if (r.at_risk) t.atRisk += 1;
    if (r.renewal_at_this_visit && stillToDo(r.status)) t.renewals += 1;
  }
  return t;
}

/** "104 a week — about 3 tech-days a day" for the visits still to do by the finish line. */
export function pace(remaining: number, finishISO: string, today = new Date()): { perWeek: number; techDaysPerDay: number; weeksLeft: number } {
  const finish = new Date(finishISO + "T00:00:00");
  const days = Math.max(1, Math.round((finish.getTime() - today.getTime()) / 86400000));
  const weeksLeft = Math.max(1, days / 7);
  const perWeek = Math.ceil(remaining / weeksLeft);
  // 8 checks make a tech-day; 5 working days a week.
  const techDaysPerDay = Math.round((perWeek / 5 / 8) * 10) / 10;
  return { perWeek, techDaysPerDay, weeksLeft: Math.round(weeksLeft) };
}

export const MONTH_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
